import {
  getMachinesSearch,
  getMachinesSearchOptions,
  getOrganizationsByOrgIdMachines,
  getOrganizationsByOrgIdMachinesQueryKey,
  type MachinePublic,
  postOrganizationsByOrgIdAssociateMachinesMutation,
} from "@aura-frontend/omnibus-az-client";
import { mergeRefs } from "@aura-frontend/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@win-reality/react-ui";
import { X } from "lucide-react";
import { forwardRef, useCallback, useMemo, useRef, useState } from "react";
import { useDebounce } from "react-use";
import { usaStates } from "typed-usa-states";

import { AutoComplete } from "../autocomplete";
import { Button } from "../ui/Button";
import { SelectedMachinesList } from "./selected-machines-list";

interface SearchMachineComboboxProps {
  valueSearchPlaceholder: string;
  organizationId: string;
}

const SearchMachineCombobox = forwardRef<
  HTMLDivElement,
  SearchMachineComboboxProps
>(({ valueSearchPlaceholder, organizationId }, ref) => {
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [selectedState, setSelectedState] = useState("");

  const queryClient = useQueryClient();
  const organizationMachinesQueryKey = getOrganizationsByOrgIdMachinesQueryKey({
    path: {
      org_id: encodeURIComponent(organizationId),
    },
  });

  const { data: selectedMachines = [], isLoading: isLoadingSelectedMachines } =
    useQuery({
      queryKey: getOrganizationsByOrgIdMachinesQueryKey({
        path: {
          org_id: encodeURIComponent(organizationId),
        },
      }),
      queryFn: async () => {
        const response = await getOrganizationsByOrgIdMachines({
          path: {
            org_id: encodeURIComponent(organizationId),
          },
        });

        if (!response.data) {
          throw new Error("Failed to fetch machines");
        }

        // Fetch details for all machines in parallel
        const machineDetailsPromises = response.data.machineIds.map(
          async (machineId) => {
            try {
              const detailsResponse = await getMachinesSearch({
                query: {
                  machineUuid: machineId,
                },
              });

              if (!detailsResponse.data) return null;
              return detailsResponse.data.machines?.[0];
            } catch {
              return null;
            }
          },
        );

        const machineDetails = await Promise.all(machineDetailsPromises);
        return machineDetails.filter(Boolean) as MachinePublic[];
      },
    });

  const containerRef = useRef<HTMLDivElement>(null);

  useDebounce(
    () => {
      setDebouncedSearchValue(searchValue);
    },
    500,
    [searchValue],
  );

  const associateMachinesMutation = useMutation({
    ...postOrganizationsByOrgIdAssociateMachinesMutation(),
    onError: () => {
      toast("Failed to associate machine", { variant: "error" });
    },
  });

  const { data: searchResults, isLoading: isSearching } = useQuery({
    ...getMachinesSearchOptions({
      query: {
        name: debouncedSearchValue,
      },
    }),
  });

  // Stabilize searchItems computation to prevent Safari reset issues
  const availableMachineOptions = useMemo(() => {
    if (!searchResults?.machines || !Array.isArray(searchResults.machines)) {
      return [];
    }

    // Ensure selectedMachines is always an array for safe filtering
    const validSelectedMachines = Array.isArray(selectedMachines)
      ? selectedMachines
      : [];

    const availableMachines = searchResults.machines.filter((machine) => {
      if (!machine || typeof machine.machineUuid !== "string") return false;

      const isNotSelected = !validSelectedMachines.some(
        (selectedMachine) =>
          selectedMachine &&
          selectedMachine.machineUuid === machine.machineUuid,
      );
      const matchesStateFilter =
        !selectedState || machine.region === selectedState;

      return isNotSelected && matchesStateFilter;
    });

    return availableMachines.map((machine) => {
      const machineValue = {
        machineUuid: machine.machineUuid,
        name: machine.name,
      };

      return {
        value: JSON.stringify(machineValue),
        label: machineValue.name,
      };
    });
  }, [searchResults?.machines, selectedMachines, selectedState]);

  const handleMachineSelect = useCallback(
    async (rawMachineData: string) => {
      const newMachine: Pick<MachinePublic, "machineUuid" | "name"> =
        JSON.parse(rawMachineData);

      if (associateMachinesMutation.isPending) {
        return;
      }

      const isAlreadySelected = selectedMachines.some(
        (machine) => machine.machineUuid === newMachine.machineUuid,
      );

      if (!isAlreadySelected) {
        const previousData = queryClient.getQueryData(
          organizationMachinesQueryKey,
        );

        // Optimistic update
        const updatedSelectedMachines = [
          ...selectedMachines,
          {
            machineUuid: newMachine.machineUuid,
            name: newMachine.name,
          },
        ];

        queryClient.setQueryData(
          organizationMachinesQueryKey,
          updatedSelectedMachines,
        );

        try {
          const machineIds = updatedSelectedMachines.map(
            (machine) => machine.machineUuid,
          );

          await associateMachinesMutation.mutateAsync({
            path: {
              org_id: encodeURIComponent(organizationId),
            },
            body: { machineIds },
          });
        } catch {
          // Revert optimistic update on error
          queryClient.setQueryData(organizationMachinesQueryKey, previousData);
        }
      }
    },
    [
      associateMachinesMutation,
      selectedMachines,
      queryClient,
      organizationMachinesQueryKey,
      organizationId,
    ],
  );

  const handleMachineRemove = useCallback(
    async (machineIdToRemove: string) => {
      if (associateMachinesMutation.isPending) {
        return;
      }

      const previousData = queryClient.getQueryData(
        organizationMachinesQueryKey,
      );

      // Optimistic update
      const remainingMachines = selectedMachines.filter(
        (machine) => machine.machineUuid !== machineIdToRemove,
      );

      queryClient.setQueryData(organizationMachinesQueryKey, remainingMachines);

      try {
        const machineIds = remainingMachines.map(
          (machine) => machine.machineUuid,
        );

        await associateMachinesMutation.mutateAsync({
          path: {
            org_id: encodeURIComponent(organizationId),
          },
          body: { machineIds },
        });
      } catch {
        // Revert optimistic update on error
        queryClient.setQueryData(organizationMachinesQueryKey, previousData);
      }
    },
    [
      associateMachinesMutation,
      selectedMachines,
      queryClient,
      organizationMachinesQueryKey,
      organizationId,
    ],
  );

  const handleSearchChange = useCallback((newValue: string) => {
    setSearchValue(newValue);
  }, []);

  const handleStateChange = useCallback((stateValue: string) => {
    setSelectedState(stateValue);
  }, []);

  const handleStateClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedState("");
  }, []);

  const handleAutoCompleteSelect = useCallback(
    (rawMachineData: string) => {
      if (!associateMachinesMutation.isPending) {
        handleMachineSelect(rawMachineData);
      }
    },
    [associateMachinesMutation.isPending, handleMachineSelect],
  );

  return (
    <div>
      <div className="w-full flex" ref={mergeRefs(ref, containerRef)}>
        <AutoComplete
          items={availableMachineOptions}
          placeholder={valueSearchPlaceholder}
          onChange={handleSearchChange}
          isLoading={isSearching || associateMachinesMutation.isPending}
          onSelect={handleAutoCompleteSelect}
        />

        <div className="relative w-1/3">
          <select
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
            aria-label="State Filter"
            className={`flex h-10 w-full appearance-none rounded-sm rounded-l-none border border-gray-300 bg-input px-3 py-2 pr-8 text-sm text-text-input placeholder:font-light placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${!selectedState ? "text-placeholder font-light" : ""}`}
          >
            <option value="" disabled hidden>
              State Filter
            </option>
            {usaStates.map((s) => (
              <option
                key={s.name}
                value={`${s.name} (${s.abbreviation})`}
                className="text-black"
              >
                {s.name}
              </option>
            ))}
          </select>

          {/* Chevron icon */}
          {!selectedState && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-4 w-4 opacity-50"
              >
                <path
                  fillRule="evenodd"
                  d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          )}

          {/* Clear button */}
          {selectedState && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-3 mt-[1px] top-1/2 -translate-y-1/2 transform h-6 w-6 hover:bg-muted"
              onClick={handleStateClear}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      <SelectedMachinesList
        selectedMachines={selectedMachines}
        isLoading={isLoadingSelectedMachines}
        onRemoveMachine={handleMachineRemove}
        isPending={associateMachinesMutation.isPending}
      />
    </div>
  );
});

SearchMachineCombobox.displayName = "SearchMachineCombobox";

export default SearchMachineCombobox;
